import React, { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { ChevronRight, Plus } from "lucide-react";
import { Autoplay } from "swiper/modules";
import { Link, NavLink } from "react-router-dom";

// Define the type for items inside case.json
interface CaseItem {
    id: string | number;
    image: string;
    heading: string;
}

// Import JSON with type assertion
import swipeContentData from "./case.json";
import Header from "@/components/landing/layout/Header";
import Footer from "@/components/landing/layout/Footer";
import GettingStarted from "@/components/landing/common/GettingStarted";
const swipeContent = swipeContentData as CaseItem[];

const CaseStudies: React.FC = () => {
    const [showCategories, setShowCategories] = useState<boolean>(false);

    return (
        <>
            <Header />
            <div className="tw-bg-black tw-object-center tw-text-white">
                {/* Header Section */}
                <header className="tw-py-32 tw-max-w-3xl tw-mx-auto">
                    <h3 className="tw-font-extralight tw-mb-1 tw-text-xl">CASE STUDY</h3>
                    <h1 className="lg:tw-text-5xl tw-text-3xl tw-font-bold tw-tracking-wide">
                        See how Protega provides <br />
                        proven results for merchants
                    </h1>
                </header>

                {/* Swiper Section */}
                <div className="tw-pl-5">
                    <Swiper
                        spaceBetween={50}
                        slidesPerView={1.2}
                        loop={true}
                        autoplay={{
                            delay: 3000,
                            disableOnInteraction: false,
                        }}
                        modules={[Autoplay]}
                        className="tw-h-[75vh]"
                    >
                        {swipeContent.map((item) => (
                            <SwiperSlide
                                key={item.id}
                                style={{ backgroundImage: `url(${item.image})` }}
                                className="tw-rounded-lg tw-bg-cover tw-bg-center tw-flex tw-items-start tw-justify-start tw-text-white tw-h-[75vh]"
                            >
                                <div
                                    data-aos="fade-up"
                                    className="md:tw-w-1/3 lg:tw-m-8 tw-p-2 md:tw-p-8 tw-rounded-xl tw-m-2 tw-bg-[#0C0C0C]"
                                >
                                    <h1 className="md:tw-text-3xl tw-text-md tw-tracking-wide tw-font-semibold tw-mb-2 md:tw-mb-6">
                                        {item.heading}
                                    </h1>
                                    <Link to={`${item?.id}`} className="tw-bg-[#00C5FB] tw-cursor-pointer tw-text-sm tw-text-[#0C0C0C] tw-rounded-3xl tw-px-6 tw-py-3">
                                        Learn More
                                    </Link>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>

                {/* Filter Section */}
                <div className="tw-bg-[#0C0C0C] tw-p-6 lg:tw-py-16 tw-mt-10">
                    <div className="tw-w-[70vw] tw-mx-auto">

                        <div
                            onClick={() => setShowCategories(!showCategories)}
                            className="tw-flex tw-justify-between tw-items-center tw-text-2xl tw-tracking-wide tw-font-semibold tw-cursor-pointer"
                        >
                            <h1>FILTER</h1>
                            <span
                                className={
                                    showCategories
                                        ? "tw-rotate-45 tw-transition-transform"
                                        : "tw-transition-transform"
                                }
                            >
                                <Plus />
                            </span>
                        </div>

                        {/* Collapsible Filter Options */}
                        <div
                            className={`tw-transition-all tw-duration-500 tw-overflow-hidden ${showCategories
                                ? "tw-max-h-[500px] tw-opacity-100"
                                : "tw-max-h-0 tw-opacity-0"
                                } tw-mt-5`}
                        >
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                                {/* Industry */}
                                <div>
                                    <label className="tw-block tw-text-sm tw-mb-2 tw-tracking-widest tw-uppercase">
                                        Industry
                                    </label>
                                    <select className="tw-w-full tw-px-2 tw-py-4 tw-rounded-md tw-bg-[rgba(255,255,255,0.1)] tw-text-white">
                                        <option className="tw-bg-[#0C0C0C]">Select...</option>
                                        <option className="tw-bg-[#0C0C0C]">Electronics</option>
                                        <option className="tw-bg-[#0C0C0C]">Furniture</option>
                                        <option className="tw-bg-[#0C0C0C]">Fashion</option>
                                    </select>
                                </div>

                                {/* Merchant Type */}
                                <div>
                                    <label className="tw-block tw-text-sm tw-mb-2 tw-tracking-widest tw-uppercase">
                                        Merchant Type
                                    </label>
                                    <select className="tw-w-full tw-px-2 tw-py-4 tw-rounded-md tw-bg-[rgba(255,255,255,0.1)] tw-text-white">
                                        <option className="tw-bg-[#0C0C0C]">Select...</option>
                                        <option className="tw-bg-[#0C0C0C]">Retail</option>
                                        <option className="tw-bg-[#0C0C0C]">Wholesale</option>
                                        <option className="tw-bg-[#0C0C0C]">D2C</option>
                                    </select>
                                </div>

                                {/* Product */}
                                <div>
                                    <label className="tw-block tw-text-md tw-mb-2 tw-tracking-widest tw-uppercase">
                                        Product
                                    </label>
                                    <select className="tw-w-full tw-px-2 tw-py-4 tw-rounded-md tw-bg-[rgba(255,255,255,0.1)] tw-text-white">
                                        <option className="tw-bg-[#0C0C0C]">Select...</option>
                                        <option className="tw-bg-[#0C0C0C]">Phones</option>
                                        <option className="tw-bg-[#0C0C0C]">Laptops</option>
                                        <option className="tw-bg-[#0C0C0C]">Home Decor</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Case Grid Section */}
                <div className="tw-grid md:tw-grid-cols-2  tw-w-[70vw] tw-mx-auto tw-mt-5 tw-gap-x-10 tw-gap-y-5">
                    {swipeContent.map((items) => (
                        <NavLink
                                    to={`/case-studies/${items.id}`}
                            key={items.id}
                            className="tw-flex tw-flex-col hover:tw-shadow-[#00C5FB] tw-h-130 hover:tw-scale-110 tw-transition-all tw-duration-300 hover:tw-shadow-[0_0_20px_#00C5FB] tw-bg-[#0C0C0C] tw-w-full tw-rounded-lg tw-overflow-hidden"
                        >
                            <img
                                src={items.image}
                                alt={items.heading}
                                className="tw-w-full tw-h-60 tw-object-cover"
                            />
                            <div className="tw-w-[80%] tw-px-6 tw-py-5 tw-flex tw-flex-col tw-flex-1">
                                <h1 className="lg:tw-text-3xl tw-pb-8 tw-font-semibold">
                                    {items.heading}
                                </h1>
                                <NavLink
                                    to={`/case-studies/${items.id}`}
                                    className="tw-mt-auto tw-flex tw-items-center tw-self-start tw-font-light"
                                >
                                    Learn More <ChevronRight />
                                </NavLink>
                            </div>
                        </NavLink>
                    ))}
                </div>
            </div>
            <GettingStarted />
            <Footer />
        </>
    );
};

export default CaseStudies;
